import { useEffect, useState } from 'react';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import { Box, Button } from '@mui/material';
import TemplatePreview from '../templates/TemplatePreview';
import { useTranslation } from 'react-i18next';
import TemplateVarSwitcher from '../templates/TemplateVarSwitcher';
import { numberToString } from '../special/numberConverter';
import moment from 'moment';
import { useDebounce } from 'use-debounce';

const ExoDocumentPreview = ({
  dataCollection,
  previewDocument,
  documentId,
  width = 500,
}) => {
  const [template, setTemplate] = useState(null);
  const [templateChange, setTemplateChange] = useState(0);
  const [templateSkonto, setTemplateSkonto] = useState('');
  const [templatePositions, setTemplatePositions] = useState('');
  const [templateOrderTable, setTemplateOrder] = useState('');
  const [templateInvoices, setTemplateInvoices] = useState('');
  const [debouncedDataCollection] = useDebounce(dataCollection, 700);
  const { t } = useTranslation();
  const config = useConfig();

  useEffect(() => {
    if (!config) return;
    setTemplate(null);
    DBRequest({
      config,
      path: documentId
        ? `templates/${documentId}`
        : `templates?templateType[eq]=${previewDocument}`,
      method: API_GET,
      onResponse: (data) =>
        handleResponse(documentId ? data.data : data.data[0]),
    });
  }, [config, previewDocument, documentId]);

  function handleResponse(data) {
    var newTemplate = data;
    var templateVars = newTemplate.vars;

    // INVOICES (PROGRESS BILLING) -------------------------------
    const invoicesStartKey = '%%hideIfNoInvoicesStart%%';
    const invoicesEndKey = '%%hideIfNoInvoicesEnd%%';
    const invoicesReplaceKey = '%%invoicesSection%%';
    const invoicesTemplateArea = getTemplateArea(
      newTemplate.content,
      invoicesStartKey,
      invoicesEndKey,
      invoicesReplaceKey
    );
    newTemplate.content = invoicesTemplateArea.modifiedContent;
    // Add the custom positions var to the var array of the template
    const customInvoicesVar = {
      value: invoicesReplaceKey,
      name: 'Document Invoices',
      preview: invoicesTemplateArea.contentBetweenKeys[0],
    };
    templateVars.push(customInvoicesVar);
    const invoicesTemplateString = invoicesTemplateArea.contentBetweenKeys[0];
    setTemplateInvoices(invoicesTemplateString);

    // ORDER (PROGRESS BILLING) -------------------------------
    // Your start and end keys
    const orderStartKey = '%%orderTableStart%%';
    const orderEndKey = '%%orderTableEnd%%';
    const orderReplaceKey = '%%orderTable%%';
    const orderTemplateArea = getTemplateArea(
      newTemplate.content,
      orderStartKey,
      orderEndKey,
      orderReplaceKey
    );
    newTemplate.content = orderTemplateArea.modifiedContent;
    // Add the custom positions var to the var array of the template
    const customOrderVar = {
      value: orderReplaceKey,
      name: 'Document Order',
      preview: orderTemplateArea.contentBetweenKeys[0],
    };
    templateVars.push(customOrderVar);
    const orderTableTemplateString = orderTemplateArea.contentBetweenKeys[0];
    setTemplateOrder(orderTableTemplateString);

    // POSITIONS -------------------------------
    // Your start and end keys
    const positionsStartKey = '%%positionRowStart%%';
    const positionsEndKey = '%%positionRowEnd%%';
    const positionsReplaceKey = '%%documentPositions%%';
    const positionsTemplateArea = getTemplateArea(
      newTemplate.content,
      positionsStartKey,
      positionsEndKey,
      positionsReplaceKey
    );
    newTemplate.content = positionsTemplateArea.modifiedContent;
    // Add the custom positions var to the var array of the template
    const customPositionsVar = {
      value: positionsReplaceKey,
      name: 'Document Positions',
      preview: positionsTemplateArea.contentBetweenKeys[0],
    };
    templateVars.push(customPositionsVar);
    setTemplatePositions(positionsTemplateArea.contentBetweenKeys[0]);

    // SKONTO --------------------------------
    const skontoStartKey = '%%discountAreaStart%%';
    const skontoEndKey = '%%discountAreaEnd%%';
    const skontoReplaceKey = '%%documentSkonto%%';
    const skontoTemplateArea = getTemplateArea(
      newTemplate.content,
      skontoStartKey,
      skontoEndKey,
      skontoReplaceKey
    );
    newTemplate.content = skontoTemplateArea.modifiedContent;
    const customSkontoVar = {
      value: skontoReplaceKey,
      name: 'Document Skonto',
      preview: '',
    };
    templateVars.push(customSkontoVar);
    setTemplateSkonto(
      `${skontoStartKey}${skontoTemplateArea.contentBetweenKeys}${skontoEndKey}`
    );
    // save template
    setTemplate(newTemplate);
    setTemplateChange(templateChange + 1);
  }

  // change templateVars to the dataCollections data
  function prepairTemplateVars(varsToPrepair) {
    if (!varsToPrepair) return;
    const valueKeyMap = getTrueTemplateVarValue(dataCollection, true);
    setTemplate({
      ...template,
      vars: replaceTemplateVars(
        varsToPrepair,
        valueKeyMap,
        dataCollection.positions || []
      ),
    });
  }

  function handleDynamicElements() {
    if (template.vars.length) {
      const templateSections = [
        'headerP1',
        'headerP2',
        'content',
        'footerP1',
        'footerP2',
      ];
      var updatedTemplate = { ...template };
      templateSections.forEach((section) => {
        // enable deliveryRange or deliveryDate

        if (
          Boolean(
            dataCollection.useDeliveryDateRange ||
              dataCollection.deliveryDateStart
          )
        ) {
          // remove deliveryDate
          const startKey = '%%deliveryDateAreaStart%%';
          const endKey = '%%deliveryDateAreaEnd%%';
          const replaceKey = '';

          const deliveryDateTemplateArea = getTemplateArea(
            template[section],
            startKey,
            endKey,
            replaceKey
          );
          updatedTemplate[section] = deliveryDateTemplateArea.modifiedContent;
        } else {
          // remove deliveryDateRange
          const startKey = '%%deliveryDateRangeAreaStart%%';
          const endKey = '%%deliveryDateRangeAreaEnd%%';
          const replaceKey = '';

          const deliveryDateRangeTemplateArea = getTemplateArea(
            template[section],
            startKey,
            endKey,
            replaceKey
          );
          updatedTemplate[section] =
            deliveryDateRangeTemplateArea.modifiedContent;
        }

        // add or remove the skonto section
        if (Boolean(parseInt(dataCollection.discount))) {
          // add skonto section
          const skontoContentVars = [
            {
              value: '%%documentSkonto%%',
              name: '',
              preview: templateSkonto,
            },
          ];
          const newContent = TemplateVarSwitcher(
            updatedTemplate[section],
            skontoContentVars
          );
          updatedTemplate[section] = newContent;
        } else {
          // remove skonto section
          const skontoStartKey = '%%discountAreaStart%%';
          const skontoEndKey = '%%discountAreaEnd%%';
          const skontoReplaceKey = '%%documentSkonto%%';

          const skontoTemplateArea = getTemplateArea(
            updatedTemplate[section],
            skontoStartKey,
            skontoEndKey,
            skontoReplaceKey
          );
          updatedTemplate[section] = skontoTemplateArea.modifiedContent;
        }
      });
      setTemplate({ ...updatedTemplate });
    }
  }

  useEffect(() => {
    if (template) prepairTemplateVars(template.vars);
  }, [
    debouncedDataCollection,
    templatePositions,
    templateChange,
    templateOrderTable,
    templateInvoices,
  ]);

  useEffect(() => {
    if (!template) return;
    handleDynamicElements();
  }, [debouncedDataCollection]);

  function getTrueTemplateVarValue(data = {}, hasInvoiceArray) {
    // positions sum template Vars
    var nettoSum = 0;
    var bruttoSum = 0;
    if (data.positions) {
      data.positions.forEach((pos) => {
        if (pos.isOptional) return;
        nettoSum += pos.price * pos.amount;
        bruttoSum += pos.price * pos.amount * (1 + pos.taxRate / 100);
      });
    }
    // dates
    const dateFormat = config.general.dateOutputFormat;

    const discountDeadline = moment()
      .add(
        data.discountPaymentDeadlineInDays || data.discountDaysToPay || 0,
        'days'
      )
      .format(dateFormat);

    const paymentDeadline = moment()
      .add(data.paymentDeadlineInDays || 0, 'days')
      .format(dateFormat);

    // get paymentMethod
    const { options } = config.invoice.paymentOptions;
    const paymentMethod = options.find(
      (obj) => data.paymentMethod === obj.value
    );

    // invoices
    function calcAlreadyInvoicedAmount(key) {
      if (!data.previousInvoices) return 0;
      var newSum = 0;
      data.previousInvoices.forEach((element) => {
        newSum += element[key];
      });
      return newSum;
    }

    const alreadyInvoicedNettoAmount = calcAlreadyInvoicedAmount('amount');
    const alreadyInvoicedBruttoAmount =
      calcAlreadyInvoicedAmount('bruttoAmount');
    const orderEffectiveInvoicedNettoAmount =
      calcAlreadyInvoicedAmount('orderEffectiveAmountNetto') ||
      calcAlreadyInvoicedAmount('amount');
    const orderEffectiveInvoicedBruttoAmount =
      calcAlreadyInvoicedAmount('orderEffectiveAmountBrutto') ||
      calcAlreadyInvoicedAmount('bruttoAmount');

    // order
    const orderNettoAmount = data.order?.volume || data.orderPrice;
    const orderBruttoAmount =
      data.order?.taxVolume + data.order?.volume ||
      data.orderTaxAmount + data.orderPrice;
    const orderTaxAmount = data.order?.taxVolume || data.orderTaxAmount;

    // discount
    const discountOrderAmountNetto =
      data.orderPrice * ((data.orderDiscountPercent || 0) / 100);
    const discountOrderAmountTax =
      data.orderTaxAmount * ((data.orderDiscountPercent || 0) / 100);

    // discounted open order
    const discountedOpenOrderNettoAmount =
      orderNettoAmount -
      discountOrderAmountNetto -
      orderEffectiveInvoicedNettoAmount;
    const discountedOpenOrderBruttoAmount =
      orderBruttoAmount -
      (discountOrderAmountNetto + discountOrderAmountTax) -
      orderEffectiveInvoicedBruttoAmount;

    // assign values to the right key/placeholder
    const valueKeyMap = {
      '%%invoicesSection%%': {
        value: hasInvoiceArray
          ? generateRenderedTemplateInvoices(templateInvoices)
          : '',
        fallback: '',
      },
      '%%orderEffectiveInvoicedNettoAmount%%': {
        value: numberToString(orderEffectiveInvoicedNettoAmount),
        fallback: 0,
      },
      '%%orderEffectiveInvoicedTaxAmount%%': {
        value: numberToString(
          orderEffectiveInvoicedBruttoAmount - orderEffectiveInvoicedNettoAmount
        ),
        fallback: 0,
      },
      '%%orderEffectiveInvoicedBruttoAmount%%': {
        value: numberToString(orderEffectiveInvoicedBruttoAmount),
        fallback: 0,
      },
      '%%discountedOpenOrderNettoAmount%%': {
        value: numberToString(discountedOpenOrderNettoAmount),
        fallback: 0,
      },
      '%%discountedOpenOrderTaxAmount%%': {
        value: numberToString(
          discountedOpenOrderBruttoAmount - discountedOpenOrderNettoAmount
        ),
        fallback: 0,
      },
      '%%discountedOpenOrderBruttoAmount%%': {
        value: numberToString(discountedOpenOrderBruttoAmount),
        fallback: 0,
      },
      '%%alreadyInvoicedNettoAmount%%': {
        value: numberToString(alreadyInvoicedNettoAmount),
        fallback: 0,
      },
      '%%alreadyInvoicedTaxAmount%%': {
        value: numberToString(
          alreadyInvoicedBruttoAmount - alreadyInvoicedNettoAmount
        ),
        fallback: 0,
      },
      '%%alreadyInvoicedBruttoAmount%%': {
        value: numberToString(alreadyInvoicedBruttoAmount),
        fallback: 0,
      },
      '%%orderNr%%': {
        value: data.orderNr,
        fallback: t('Empty'),
      },
      '%%orderTitle%%': {
        value: data.orderTitle,
        fallback: t('Empty'),
      },
      '%%orderDiscountPercent%%': {
        value: numberToString(data.orderDiscountPercent || 0, 1),
        fallback: 0,
      },
      '%%orderNettoAmount%%': {
        value: numberToString(orderNettoAmount),
        fallback: t('Empty'),
      },
      '%%orderBruttoAmount%%': {
        value: numberToString(orderBruttoAmount),
        fallback: t('Empty'),
      },
      '%%orderTaxAmount%%': {
        value: numberToString(orderTaxAmount),
        fallback: t('Empty'),
      },
      '%%orderDiscountNettoAmount%%': {
        value: numberToString(discountOrderAmountNetto),
        fallback: t('Empty'),
      },
      '%%orderDiscountTaxAmount%%': {
        value: numberToString(discountOrderAmountTax),
        fallback: t('Empty'),
      },
      '%%orderDiscountBruttoAmount%%': {
        value: numberToString(
          discountOrderAmountNetto + discountOrderAmountTax
        ),
        fallback: t('Empty'),
      },
      '%%paymentMethod%%': {
        value: t(paymentMethod?.documentLabel),
        fallback: t('Empty'),
      },
      '%%paymentDeadlineDate%%': {
        value: paymentDeadline,
        fallback: '01.01.2022',
      },
      '%%paymentDeadlineDays%%': {
        value: data.paymentDeadlineInDays,
        fallback: '01.01.2022',
      },
      '%%positionsSumBrutto%%': {
        value: numberToString(bruttoSum),
        fallback: '0.00',
      },
      '%%positionsSumNetto%%': {
        value: numberToString(nettoSum),
        fallback: '0.00',
      },
      '%%positionsSumTax%%': {
        value: numberToString(bruttoSum - nettoSum),
        fallback: '0.00',
      },
      '%%documentPositions%%': {
        value: generateRenderedTemplatePosition(
          templatePositions,
          data.positions,
          template.vars
        ),
        fallback: '',
      },
      '%%documentDate%%': {
        value: moment(data.date).format(dateFormat),
        fallback: t('Empty'),
      },
      '%%letterContent%%': {
        value: data.content,
        fallback: t('Empty'),
      },
      '%%documentNr%%': {
        value: data.nr,
        fallback: t('*AUTOMATIC*'),
      },
      '%%documentTitle%%': {
        value: data.title || data.subject,
        fallback: t('Empty'),
      },
      '%%documentNote%%': {
        value: data.note,
        fallback: '',
      },
      '%%documentValidUntil%%': {
        value: moment(data.validUntil).format(dateFormat),
        fallback: t('Empty'),
      },
      '%%documentContactPersonSalutation%%': {
        value: data.contactPerson ? data.contactPerson.salutation : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonFirstName%%': {
        value: data.contactPerson ? data.contactPerson.firstName : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonLastName%%': {
        value: data.contactPerson ? data.contactPerson.lastName : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonEmail%%': {
        value: data.contactPerson ? data.contactPerson.email : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonTelephoneNumber%%': {
        value: data.contactPerson ? data.contactPerson.phone : null,
        fallback: t('Empty'),
      },
      '%%recipientClientNumber%%': {
        value: data.recipient ? data.recipient.nr : '',
        fallback: t('Empty'),
      },
      '%%recipientCompanyName%%': {
        value: data.recipient ? data.recipient.name : '',
        fallback: t('Empty'),
      },
      '%%recipientCompanyNameShort%%': {
        value: data.recipient ? data.recipient.nameShort : '',
        fallback: t('Empty'),
      },
      '%%recipientWebsiteUrl%%': {
        value: data.recipient ? data.recipient.url : '',
        fallback: t('Empty'),
      },
      '%%recipientIban%%': {
        value: data.recipient ? data.recipient.iban : '',
        fallback: t('Empty'),
      },
      '%%recipientInvoiceMail%%': {
        value: data.recipient ? data.recipient.invoiceMail : '',
        fallback: t('Empty'),
      },
      '%%recipientContactMail%%': {
        value: data.recipient ? data.recipient.mail : '',
        fallback: t('Empty'),
      },
      '%%recipientPhoneNumber%%': {
        value: data.recipient ? data.recipient.phone : '',
        fallback: t('Empty'),
      },
      '%%recipientStreet%%': {
        value: data.recipient ? data.recipient.address.street : '',
        fallback: t('Empty'),
      },
      '%%recipientHouseNumber%%': {
        value: data.recipient ? data.recipient.address.houseNumber : '',
        fallback: '',
      },
      '%%recipientZipCode%%': {
        value: data.recipient ? data.recipient.address.zipCode : '',
        fallback: '',
      },
      '%%recipientCity%%': {
        value: data.recipient ? data.recipient.address.city : '',
        fallback: t('Empty'),
      },
      '%%recipientCountry%%': {
        value: data.recipient ? data.recipient.address.country : '',
        fallback: t('Empty'),
      },
      '%%recipientAddressAddition%%': {
        value: data.recipient ? data.recipient.address.addition : '',
        fallback: '',
      },
      '%%recipientSalutation%%': {
        value: data.recipient ? data.recipient.salutation : '',
        fallback: t('Empty'),
      },
      '%%recipientFirstName%%': {
        value: data.recipient ? data.recipient.firstName : '',
        fallback: t('Empty'),
      },
      '%%recipientPhoneNumber%%': {
        value: data.recipient
          ? getPrimaryContactData(
              data.recipient.telephoneNumbers,
              'phoneNumber'
            )
          : '',
        fallback: t('Empty'),
      },
      '%%recipientInvoiceMail%%': {
        value: data.recipient
          ? getPrimaryContactData(
              data.recipient.emails,
              'emailAddress',
              'isInvoiceMail'
            )
          : '',
        fallback: t('Empty'),
      },
      '%%recipientContactMail%%': {
        value: data.recipient
          ? getPrimaryContactData(data.recipient.emails, 'emailAddress')
          : '',
        fallback: t('Empty'),
      },
      '%%recipientMail%%': {
        value: data.recipient
          ? getPrimaryContactData(data.recipient.emails, 'emailAddress')
          : '',
        fallback: t('Empty'),
      },
      '%%recipientLastName%%': {
        value: data.recipient ? data.recipient.lastName : '',
        fallback: t('Empty'),
      },
      '%%cashDiscountRate%%': {
        value: numberToString(data.discountRate, 2),
        fallback: '',
      },
      '%%discountedPrice%%': {
        value: numberToString(
          bruttoSum - (data.discountRate / 100) * bruttoSum
        ),
        fallback: '',
      },
      '%%cashDiscountDaysToPay%%': {
        value: data.discountPaymentDeadlineInDays,
        fallback: '',
      },
      '%%documentDeliveryStartDate%%': {
        value: moment(data.deliveryDateStart).format(dateFormat),
        fallback: '',
      },
      '%%documentDeliveryEndDate%%': {
        value: moment(data.deliveryDateEnd).format(dateFormat),
        fallback: '',
      },
      '%%cashDiscountDeadline%%': {
        value: discountDeadline,
        fallback: '',
      },
    };

    const orderTable = {
      value: dataCollection.showOrderPositions
        ? generateRenderedOrderTable(
            templateOrderTable,
            dataCollection,
            template.vars,
            valueKeyMap
          )
        : '',
      fallback: '',
    };

    valueKeyMap['%%orderTable%%'] = orderTable;
    return valueKeyMap;
  }

  function getPrimaryContactData(arr, key, validationKey = 'isPrimary') {
    if (!arr) return undefined;
    const primaryContactData = arr.find((obj) => Boolean(obj[validationKey]));
    if (!primaryContactData) return undefined;
    return key ? primaryContactData[key] : primaryContactData;
  }

  function generateRenderedTemplateInvoices(invoiceTemplate) {
    if (
      !invoiceTemplate ||
      !dataCollection?.previousInvoices ||
      !dataCollection?.previousInvoices.length
    )
      return '';

    // get invoice loop part
    const invoicesStartKey = '%%invoicesStart%%';
    const invoicesEndKey = '%%invoicesEnd%%';
    const invoicesReplaceKey = '%%invoices%%';
    const invoicesTemplateArea = getTemplateArea(
      invoiceTemplate,
      invoicesStartKey,
      invoicesEndKey,
      invoicesReplaceKey
    );
    const baseTemplate = invoicesTemplateArea.modifiedContent;
    const invoiceLoopTemplate = invoicesTemplateArea.contentBetweenKeys[0];

    var renderedInvoices = '';

    dataCollection?.previousInvoices?.forEach((invoice) => {
      const valueKeyMap = getTrueTemplateVarValue(invoice, false);

      const { positions, showPositions } = invoice;

      var renderedTemplate = invoiceLoopTemplate;

      if (showPositions) {
        const positionsStartKey = '%%positionRowStart%%';
        const positionsEndKey = '%%positionRowEnd%%';
        const positionsReplaceKey = '%%invoicePositions%%';

        // replace positions row with placeholder
        const positionsTemplateArea = getTemplateArea(
          renderedTemplate,
          positionsStartKey,
          positionsEndKey,
          positionsReplaceKey
        );
        renderedTemplate = positionsTemplateArea.modifiedContent;
        const positionsTemplate = positionsTemplateArea.contentBetweenKeys[0];

        //render positions template with real data
        const renderedPositions = generateRenderedTemplatePosition(
          positionsTemplate,
          positions,
          template.vars
        );

        // replace temporary placeholder for positions row
        renderedTemplate = renderedTemplate.replace(
          positionsReplaceKey,
          renderedPositions
        );
      } else {
        const positionsTableStartKey = '%%invoicePositionsTableStart%%';
        const positionsTableEndKey = '%%invoicePositionsTableEnd%%';
        const removedPositionsTableArea = getTemplateArea(
          renderedTemplate,
          positionsTableStartKey,
          positionsTableEndKey,
          ''
        );
        renderedTemplate = removedPositionsTableArea.modifiedContent;
      }

      return (renderedInvoices += TemplateVarSwitcher(
        renderedTemplate,
        replaceTemplateVars(template.vars, valueKeyMap)
      ));
    });

    const valueKeyMap = getTrueTemplateVarValue(dataCollection, false);

    // render baseTemplate
    const renderedBaseTemplate = TemplateVarSwitcher(
      baseTemplate,
      replaceTemplateVars(template.vars, valueKeyMap)
    );

    const renderdInvoiceSection = renderedBaseTemplate.replace(
      '%%invoices%%',
      renderedInvoices
    );

    return renderdInvoiceSection;
  }

  return (
    <Box
      className="w-full h-full flex flex-col  justify-center items-center"
      sx={{
        ul: {
          listStyle: 'disc',
        },
        '& .templateVar': {
          display: 'inline',
          animation: 'templateVarChange 5s ease',
          '& p, h1, h2, h3, h4, td': {
            animation: 'templateVarChange 5s ease',
          },
        },
        '@keyframes templateVarChange': {
          '0%': {
            color: 'red',
          },
          '100%': {
            color: 'initial',
          },
        },
        '& .templateVarFallback': {
          color: 'red',
        },
      }}
    >
      {template !== null && (
        <TemplatePreview
          data={template}
          paginate={false}
          fit
          className="flex h-full justify-center items-center"
        />
      )}
    </Box>
  );
};

function getTemplateArea(content, startKey, endKey, replacerKey) {
  // Use a global regular expression to match all areas between startKey and endKey
  const regex = new RegExp(`${startKey}(.*?)${endKey}`, 'gs'); // 's' flag allows dot (.) to match newline characters
  let contentBetweenKeys = [];

  // Replace all matches and store the matched content between the keys
  const modifiedContent = content.replace(regex, (match, group1) => {
    contentBetweenKeys.push(group1); // Store the content between start and end keys
    return replacerKey; // Replace the matched area with replacerKey
  });

  return { modifiedContent, contentBetweenKeys };
}

function generateRenderedTemplatePosition(
  template,
  positions,
  globalTemplateVars
) {
  if (!template) return '';
  var renderedTemplatePositions = '';
  if (positions) {
    positions.forEach((element) => {
      const positionVarsMap = {
        '%%positionPosition%%': {
          value: element.position,
          fallback: '',
        },
        '%%positionName%%': {
          value: element.name,
          fallback: '',
        },
        '%%positionAmount%%': {
          value: element.isGroup ? '' : numberToString(element.amount),
          fallback: '',
        },
        '%%positionUnit%%': {
          value: element.isGroup ? '' : element.unit,
          fallback: '',
        },
        '%%positionPrice%%': {
          value: element.isGroup ? '' : numberToString(element.price),
          fallback: '',
        },
        '%%positionSumBrutto%%': {
          value: element.isGroup
            ? ''
            : numberToString(
                element.price * element.amount * (1 + element.taxRate / 100)
              ),
          fallback: '',
        },
        '%%positionSumNetto%%': {
          value: element.isGroup
            ? ''
            : numberToString(element.price * element.amount),
          fallback: '',
        },
        '%%positionSumTax%%': {
          value: element.isGroup
            ? ''
            : numberToString(element.price * (element.taxRate / 100)),
          fallback: '',
        },
        '%%positionTaxationRate%%': {
          value: element.isGroup ? '' : element.taxRate,
          fallback: '',
        },
        '%%positionDescription%%': {
          value: element.isGroup ? '' : element.description,
          fallback: '',
        },
        '%%positionImage%%': {
          value: element.isGroup
            ? ''
            : element.image
            ? element.image.temporaryUrl
            : '',
          fallback: '',
        },
      };

      // remove elements that are overwritten
      const arrOfValuesToRemove = ['%%positionImage%%', '%%positionName%%'];
      const filteredGlobalTemplateVars = globalTemplateVars.filter(
        (x) => !arrOfValuesToRemove.includes(x.value)
      );

      const positionTemplateVars = [
        ...filteredGlobalTemplateVars,
        {
          name: '',
          value: '%%positionPosition%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionName%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionAmount%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionUnit%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionPrice%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionSumBrutto%%',
          preview: '',
        },

        {
          name: '',
          value: '%%positionSumNetto%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionSumTax%%',
          preview: '',
        },

        {
          name: '',
          value: '%%positionTaxationRate%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionDescription%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionImage%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionImageAreaStart%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionImageAreaEnd%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionOptionalAdditionStart%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionOptionalAdditionEnd%%',
          preview: '',
          noHighlight: true,
        },
      ];

      // remove position is optional addition
      var templateToRender = template;

      if (element.isGroup) {
        const startKey = '%%positionGroupHiddenStart%%';
        const endKey = '%%positionGroupHiddenEnd%%';
        const replaceKey = '';

        const imageTemplateArea = getTemplateArea(
          templateToRender,
          startKey,
          endKey,
          replaceKey
        );
        templateToRender = imageTemplateArea.modifiedContent;
      }

      if (!element.isOptional) {
        const startKey = '%%positionOptionalAdditionStart%%';
        const endKey = '%%positionOptionalAdditionEnd%%';
        const replaceKey = '';

        const imageTemplateArea = getTemplateArea(
          templateToRender,
          startKey,
          endKey,
          replaceKey
        );
        templateToRender = imageTemplateArea.modifiedContent;
      }

      // remove position img if empty
      if (!element.image) {
        const startKey = '%%positionImageAreaStart%%';
        const endKey = '%%positionImageAreaEnd%%';
        const replaceKey = '';

        const imageTemplateArea = getTemplateArea(
          templateToRender,
          startKey,
          endKey,
          replaceKey
        );
        templateToRender = imageTemplateArea.modifiedContent;
      }

      // remove descripiton segmentation
      const startKey = '%%positionDescriptionFragmentStart%%';
      const endKey = '%%positionDescriptionFragmentEnd%%';
      const replaceKey = '';

      const imageTemplateArea = getTemplateArea(
        templateToRender,
        startKey,
        endKey,
        replaceKey
      );
      templateToRender = imageTemplateArea.modifiedContent;

      renderedTemplatePositions += TemplateVarSwitcher(
        templateToRender,
        replaceTemplateVars(positionTemplateVars, positionVarsMap)
      );
    });
  }

  return renderedTemplatePositions;
}

function generateRenderedOrderTable(
  template,
  dataCollection,
  globalTemplateVars,
  valueKeyMap
) {
  if (!dataCollection || !template) return;
  const {
    orderPositions,
    orderTitle,
    orderNr,
    orderDate,
    orderPrice,
    showOrderPositions,
  } = dataCollection;

  const positionsStartKey = '%%positionRowStart%%';
  const positionsEndKey = '%%positionRowEnd%%';
  const positionsReplaceKey = '%%orderPositions%%';

  var renderedTemplate = template;
  // replace positions row with placeholder
  const positionsTemplateArea = getTemplateArea(
    renderedTemplate,
    positionsStartKey,
    positionsEndKey,
    positionsReplaceKey
  );
  renderedTemplate = positionsTemplateArea.modifiedContent;
  const positionsTemplate = positionsTemplateArea.contentBetweenKeys[0];

  //render positions template with real data
  const renderedPositions = showOrderPositions
    ? generateRenderedTemplatePosition(
        positionsTemplate,
        orderPositions,
        globalTemplateVars
      )
    : '';

  // replace temporary placeholder for positions row
  renderedTemplate = renderedTemplate.replace(
    positionsReplaceKey,
    renderedPositions
  );

  return TemplateVarSwitcher(
    renderedTemplate,
    replaceTemplateVars(globalTemplateVars, valueKeyMap)
  );
}

function replaceTemplateVars(templateVars, varsMap, positions = []) {
  const newTemplateVars = [];

  // normal template Vars
  templateVars.forEach((element) => {
    if (varsMap[element.value]) {
      var updatedVar = element;

      if (element.noHighlight) {
        updatedVar.preview = varsMap[element.value].value;
      } else {
        if (varsMap[element.value].value) {
          updatedVar.preview = `<span class="templateVar" style=" display: inline; font-family: inherit;
    font-size: inherit;
    line-height: inherit; text-align: inherit;">${
      varsMap[element.value].value
    }</span>`;
        } else {
          updatedVar.preview = `<span class="templateVarFallback" style=" display: inline; font-family: inherit;
        font-size: inherit;
        line-height: inherit;  text-align: inherit;">${
          varsMap[element.value].fallback
        }</span>`;
        }
      }
      newTemplateVars.push(updatedVar);
    } else {
      newTemplateVars.push(element);
    }
  });
  return newTemplateVars;
}

export default ExoDocumentPreview;
